import { type PropsWithChildren, createContext, useContext } from 'react';

import { type SurveyResponse } from '@/services/api';

import type { AdminSurveyPageParams } from './PageAdminSurvey.helper';
import { type useGetSurveyQuery } from './useAdminSurveyQueries';

export type SurveyDisplayMode = 'detail' | 'questions' | 'reports';

type PageAdminSurveyContextValue = AdminSurveyPageParams & {
  surveyQuery: ReturnType<typeof useGetSurveyQuery>;
  handlePrevSurvey: () => void;
  handleNextSurvey: () => void;
  totalQuestionCount?: number;
  canGoNext: boolean;
  canGoPrev: boolean;
  displayMode: SurveyDisplayMode;
  setDisplayMode: (mode: SurveyDisplayMode) => void;
  isEditable: boolean;
  surveyData?: SurveyResponse;
};

const PageAdminSurveyContext =
  createContext<PageAdminSurveyContextValue | null>(null);

export const usePageAdminSurveyContext = () => {
  const context = useContext(PageAdminSurveyContext);
  if (!context) {
    throw new Error(
      'usePageAdminSurveyContext must be used within a PageAdminSurveyProvider'
    );
  }
  return context;
};

export const PageAdminSurveyProvider: React.FC<
  PropsWithChildren<PageAdminSurveyContextValue>
> = ({ children, ...value }) => {
  return (
    <PageAdminSurveyContext.Provider value={value}>
      {children}
    </PageAdminSurveyContext.Provider>
  );
};
